import { Select as AntSelect } from 'antd';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useGet } from 'hooks';
import debounce from 'lodash.debounce';
import { Tooltip } from 'components/common';
import { renderOptionIncludeExclude } from 'components/common/OptionRender';
import { getStringValue, isExcluded, toExcluded } from 'utils/search';

import Loader from './Loader';
import styled from 'styled-components';

const SelectContainer = styled(AntSelect)`
  .select-include-exclude-empty {
    display: none;
  }
`;

const renderOptions = (
  dataTestId,
  renderOption,
  options,
  disabledTooltipText,
  value,
  handleIncludeChange,
  handleExcludeChange
) => {
  return options?.map((o, i) => {
    let selected;
    if (Array.isArray(value)) {
      selected = value?.find(v => v.value === o.value);
    } else if (value.value === o.value) {
      selected = value;
    }
    const isExcluded = selected && selected.isExcluded;
    return renderOptionIncludeExclude(
      o,
      i,
      !!selected && !isExcluded,
      isExcluded,
      handleIncludeChange,
      handleExcludeChange,
      disabledTooltipText
    );
  });
};

const SelectIncludeExclude = React.forwardRef(
  (
    {
      options = [],
      renderOption,
      'data-testid': dataTestId,
      onCreateNewItem,
      buttonText,
      helpText,
      placeholder,
      value = [],
      remoteSearchOnClick,
      disableTooltip,
      otherRemoteParams,
      onChange,
      emptyOption,
      minInputSize = 0,
      optionProp,
      optionLabelProp,
      disabledKey,
      onBlurChange,
      disabledTooltipText,
      mode,
      ...rest
    },
    _
  ) => {
    const refSelect = useRef();

    const [{ res, loading }, getOptions] = useGet({
      url: options,
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const debounced = useCallback(
      debounce(
        value => getOptions({ query: { name: value, ...otherRemoteParams } }),
        300
      ),
      [getOptions, otherRemoteParams]
    );

    const isRemoteOptions = useRef(options && typeof options === 'string');
    const [localOptions, setLocalOptions] = useState(
      !isRemoteOptions.current
        ? options.map(x =>
            typeof x === 'string'
              ? {
                  value: getStringValue(x),
                  label: x,
                }
              : x
          )
        : []
    );

    const [optionDictionary, setOptionDictionary] = useState({});
    const [searchValue, setSearchValue] = useState(undefined);

    useEffect(() => {
      if (res && res !== localOptions) {
        if (optionProp) {
          const { optionLabel, optionValue } = optionProp;
          setLocalOptions(
            res.map(r => ({
              label: r[optionLabel],
              value: r[optionValue],
              isDisabled: disabledKey && r[disabledKey],
            }))
          );

          setOptionDictionary(prevState => ({
            ...prevState,
            ...Object.fromEntries(res.map(s => [s[optionValue], s])),
          }));
        } else {
          setLocalOptions(
            res.map(x => ({
              value: getStringValue(x),
              label: x,
            }))
          );
        }
      }
      //eslint-disable-next-line react-hooks/exhaustive-deps
    }, [res, disabledKey]);

    useEffect(() => {
      if (optionProp) {
        const { optionValue } = optionProp;

        const newOptions = value.filter(r => !optionDictionary[r[optionValue]]);

        if (newOptions.length > 0) {
          const updatedDictionary = {
            ...optionDictionary,
            ...Object.fromEntries(newOptions.map(s => [s[optionValue], s])),
          };
          setOptionDictionary(updatedDictionary);
        }
      }
      //eslint-disable-next-line react-hooks/exhaustive-deps
    }, [optionProp, value]);

    useEffect(() => {
      if (!isRemoteOptions.current) {
        setLocalOptions(
          options.map(x =>
            typeof x === 'string'
              ? {
                  value: getStringValue(x),
                  label: x,
                }
              : x
          )
        );
      }
    }, [options]);

    const onSearchChange = value => {
      if (value !== searchValue) {
        setSearchValue(value);
        if (typeof options === 'string') {
          debounced(value);
        }
      }
    };
    const getValue = prop => (optionProp ? prop[optionProp.optionValue] : prop);

    // on close token/all icons click
    const handleChange = (newValue, option) => {
      // single select
      if (!newValue) {
        onChange('', option);
        return;
      }
      const nextValue = value.filter(v =>
        newValue.some(
          nv => getValue(v) === nv.value || getValue(v) === toExcluded(nv.value)
        )
      );
      onChange(nextValue, option);
    };

    const handleIncludeChange = o => {
      if (mode !== 'multiple') {
        if (value === o) {
          onChange('');
        } else {
          onChange(o);
        }
        return;
      }

      if (
        value.some(x => getValue(x) === o && (!optionProp || !x.isExcluded))
      ) {
        onChange(value.filter(x => getValue(x) !== o));
        return;
      }

      if (
        value.some(x =>
          optionProp ? getValue(x) === o && x.isExcluded : x === toExcluded(o)
        )
      ) {
        if (optionProp) {
          onChange(
            value.map(x =>
              getValue(x) === o ? { ...x, isExcluded: false } : x
            )
          );
        } else {
          onChange(value.map(x => (x === toExcluded(o) ? o : x)));
        }
        return;
      }

      const nextValue = value ? [...value] : [];
      nextValue.push(optionProp ? optionDictionary[o] : o);
      onChange(nextValue);
      setSearchValue(null);
    };

    const handleExcludeChange = o => {
      if (mode !== 'multiple') {
        if (value === toExcluded(o)) {
          onChange('');
        } else {
          onChange(toExcluded(o));
        }
        return;
      }

      let newValues = [...value];
      const existing = value.find(
        x => getValue(x) === o || getValue(x) === toExcluded(o)
      );
      if (!existing) {
        newValues.push(
          optionProp
            ? { ...optionDictionary[o], isExcluded: true }
            : toExcluded(o)
        );
      } else if (!isExcluded(existing)) {
        newValues = optionProp
          ? value.map(x => (getValue(x) === o ? { ...x, isExcluded: true } : x))
          : value.map(x => (x === o ? toExcluded(x) : x));
      } else {
        newValues = value.filter(x =>
          optionProp ? getValue(x) !== o : x !== toExcluded(o)
        );
      }
      onChange(newValues);
      setSearchValue(null);
    };

    const handleBlur = () => {
      onSearchChange('');
      if (onBlurChange) onBlurChange();
    };

    const handleClick = () => {
      onSearchChange('');
    };

    const localValue = optionProp
      ? value?.map(v => ({
          value: v[optionProp.optionValue],
          label: v.isExcluded
            ? toExcluded(v[optionProp.optionLabel])
            : v[optionProp.optionLabel],
          isDisabled: disabledKey && v[disabledKey],
          isTest: true,
          isExcluded: v.isExcluded,
        }))
      : !value.map
      ? { value: getStringValue(value), isExcluded: isExcluded(value) }
      : value?.map(v => ({
          value: getStringValue(v),
          label: v,
          isExcluded: isExcluded(v),
        }));
    return (
      <Tooltip
        title={disableTooltip ? '' : value?.length ? placeholder : helpText}
        placement='right'
      >
        <SelectContainer
          {...rest}
          mode={mode}
          popupClassName={
            !loading && isRemoteOptions.current && !res?.length
              ? 'select-include-exclude-empty'
              : 'select-include-exclude'
          }
          labelInValue
          showArrow
          value={localValue}
          placeholder={placeholder}
          filterOption={!isRemoteOptions.current}
          ref={refSelect}
          onSearch={onSearchChange}
          searchValue={searchValue}
          showSearch={true}
          optionFilterProp='label'
          onClick={_ =>
            remoteSearchOnClick &&
            (searchValue === undefined
              ? handleClick()
              : onSearchChange(searchValue))
          }
          virtual={false}
          onBlur={handleBlur}
          onChange={handleChange}
          data-testid={dataTestId}
          optionLabelProp={
            optionProp?.optionLabel || mode === 'multiple' ? 'label' : 'display'
          }
          notFoundContent={<Loader />}
          dropdownRender={menu => <>{menu}</>}
          getPopupContainer={triggerNode => triggerNode.parentElement}
          menuItemSelectedIcon={props => null}
        >
          {!loading &&
            localOptions.length &&
            renderOptions(
              dataTestId,
              renderOption,
              localOptions,
              disabledTooltipText,
              localValue,
              handleIncludeChange,
              handleExcludeChange
            )}
        </SelectContainer>
      </Tooltip>
    );
  }
);

export default SelectIncludeExclude;
